import React, {
  useCallback,
  useEffect,
} from 'react';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  useConfirmDialog,
  useMixpanelTrack,
  useNotify,
} from 'core/hooks';
import {
  VisitNoteDTO,
} from 'dtos';
import {
  OrgPCCActivationStatus,
} from 'dtos/orgPCCActivationStatus';
import {
  PCCProgram,
} from 'dtos/pCCProgram';
import isNil from 'lodash/isNil';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useGetPCCActivationSettings,
  useGetPCCStatusByPatientId,
} from 'views/EMR/hooks/pcc';
import {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';
import {
  getEligiblePCCPrograms,
  getPCCPrograms,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/utils';
import {
  finalizedVisitNoteMessage,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteFormActions';
import {
  PCCEnrollResolvedParams,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/types';
import {
  pccEventChannel,
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type UseVisitNotePCCFinalizeParams = {
  visitNoteId?: number;
  patientId?: number;
  encounterId?: number;
  onSubmit: (formData: VisitNoteData) => Promise<VisitNoteDTO | null>;
  refetch: () => Promise<VisitNoteDTO | undefined>;
};

type UseVisitNotePCCFinalizeResult = {
  handleFinalize: (formData: VisitNoteData) => Promise<VisitNoteDTO | null>;
};

export default function useVisitNotePCCFinalize({
  visitNoteId,
  patientId = 0,
  encounterId = 0,
  onSubmit,
  refetch,
}: UseVisitNotePCCFinalizeParams): UseVisitNotePCCFinalizeResult {
  const { data: activationSettings } = useGetPCCActivationSettings();
  const isActivated = activationSettings?.activationStatus
    === OrgPCCActivationStatus.Active;
  const {
    refetch: refetchPCCStatus,
  } = useGetPCCStatusByPatientId(patientId, isActivated);
  const isCcmApplicable = activationSettings?.isCcmApplicable === true;
  const notify = useNotify();
  const mixpanelTrack = useMixpanelTrack();
  const { confirm, closeConfirm } = useConfirmDialog();

  const submitFinalize = useCallback(async (
    formData: VisitNoteData,
    version?: string | null,
  ) => {
    confirm({
      showActions: false,
      hideCloseBtn: true,
      disableBackdropClick: true,
      className: '!w-120',
      content: (
        <div className="gap-4 flex flex-col justify-center items-center">
          <Spinner size={32} />
          <p>Finalizing Visit Note...</p>
        </div>
      ),
    });

    try {
      const updatedData = await onSubmit({
        ...formData,
        isFinalized: true,
        version: version ?? formData.version,
      });
      notify({
        message: finalizedVisitNoteMessage,
        variant: 'success',
      });
      await refetch();
      visitNoteEventChannel.emit('onVisitNoteFinalized');
      mixpanelTrack('clickedFinalizeVisitNote');
      return updatedData;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      closeConfirm();
    }
  }, [closeConfirm, confirm, mixpanelTrack, notify, onSubmit, refetch]);

  const handleFinalize = useCallback(async (formData: VisitNoteData) => {
    const preSave = await onSubmit(formData);
    if (isNil(preSave)) {
      return null;
    }

    const { data: latestPCCStatus } = await refetchPCCStatus();
    const eligible = getEligiblePCCPrograms(
      getPCCPrograms(latestPCCStatus, isCcmApplicable),
    );

    if (isActivated && eligible.length > 0) {
      pccEventChannel.emit('onShowPCCEnroll', {
        patientId,
        encounterId,
        visitNoteId: preSave.visitNoteId ?? 0,
        programs: eligible.map((program) => program.type),
        selectedPrograms: eligible.map((program) => program.type),
        variant: 'programs',
        payload: {
          formData: formData as VisitNoteDTO,
          version: preSave.version,
        },
      });
      return null;
    }

    return submitFinalize(formData, preSave.version);
  }, [
    encounterId,
    isActivated,
    isCcmApplicable,
    onSubmit,
    patientId,
    refetchPCCStatus,
    submitFinalize,
  ]);

  const handleEnrollResolved = useCallback(async ({
    visitNoteId: resolvedVisitNoteId,
    plan,
    enrolled,
    programs,
    payload,
  }: PCCEnrollResolvedParams) => {
    if (resolvedVisitNoteId !== visitNoteId) {
      return;
    }

    const isInNoteRTMEnrollment = enrolled
      && isNil(payload)
      && programs.includes(PCCProgram.RTM);

    if (isInNoteRTMEnrollment) {
      await refetch();
      return;
    }

    if (isNil(payload)) {
      return;
    }

    if (enrolled && notEmptyString(plan)) {
      const latestNote = await refetch();
      if (isNil(latestNote)) {
        notify({
          variant: 'error',
          message: 'Unable to refresh the visit note after enrollment. Please try finalizing again.',
        });
        return;
      }

      await submitFinalize(latestNote as VisitNoteData, latestNote.version);
      return;
    }

    await submitFinalize(payload.formData as VisitNoteData, payload.version);
  }, [notify, refetch, submitFinalize, visitNoteId]);

  useEffect(
    () => pccEventChannel.on('onPCCEnrollResolved', handleEnrollResolved),
    [handleEnrollResolved],
  );

  return { handleFinalize };
}
